import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import React from 'react';

import {AgentApprovalResponse} from 'graphql-client/__generated/graphqlGlobalTypes';
import ChatUploadedFileChip from 'llm/chat-library/messages/ChatUploadedFileChip';
import {
  ClientOptimisticUserMessage,
  DisplayMessageChatEventNode,
} from 'llm/chat-library/messages/MessageTypes';
import {
  isVoiceTranscriptionUnavailableText,
  VOICE_TRANSCRIPTION_UNAVAILABLE_TEXT,
} from 'llm/chat-library/messages/VoiceTranscriptCopy';
import {assertNever} from 'util/Types';

import {ChatMessageBubble} from './ChatMessageBubble';
import {ChatUserMessageSender, MessageCreator, resolveUserMessageSenderCreator} from './ChatMessageSender';
import {ChatMessageSenderContext} from './ChatMessageSenderContext';

export interface ChatUserMessageBubbleProps {
  event: DisplayMessageChatEventNode | ClientOptimisticUserMessage;
  chatSessionId: string | null;
  messageSenderContext?: ChatMessageSenderContext | null;
  /** Chat session creator; used as sender fallback when the routine runs as creator. */
  sessionCreatorFallback?: MessageCreator | null;
  isFirstMessageInThread?: boolean;
  showCurrentUserSender?: boolean;
}

/**
 * The italic footer note shown inside a user message that answered an approval request, derived
 * directly from the message's own ``agentApprovalDecision``. Returns null for ordinary messages
 * (no decision) so no note is rendered.
 */
function getApprovalIndicatorText(
  event: DisplayMessageChatEventNode | ClientOptimisticUserMessage
): string | null {
  if (event.__typename !== 'DisplayMessageChatEventNode') {
    return null;
  }
  const decision = event.agentApprovalDecision;
  if (decision == null) {
    return null;
  }
  switch (decision.response) {
    case AgentApprovalResponse.APPROVED_THIS_TURN:
    case AgentApprovalResponse.APPROVED_THIS_CHAT:
      return 'Approval granted';
    case AgentApprovalResponse.EXPLICITLY_DENIED:
    case AgentApprovalResponse.IMPLICITLY_DENIED:
      return 'Approval not granted';
    default:
      assertNever(decision.response);
      return null;
  }
}

const useStyles = createStyles((theme) => ({
  chipContainer: {
    marginTop: theme.spacing.sm,
  },
  approvalIndicator: {
    marginTop: theme.spacing.xs,
    fontSize: theme.fontSizes.sm,
    fontStyle: 'italic',
    opacity: 0.7,
  },
  unavailableTranscript: {
    color: theme.colors.gray[6],
  },
}));

const ChatUserMessageBubble = React.forwardRef<HTMLDivElement, ChatUserMessageBubbleProps>(
  (
    {
      event,
      chatSessionId,
      messageSenderContext = null,
      sessionCreatorFallback = null,
      isFirstMessageInThread = false,
      showCurrentUserSender = false,
    },
    ref
  ): JSX.Element => {
    const {classes} = useStyles();
    const approvalIndicatorText = getApprovalIndicatorText(event);
    const isOptimistic = event.__typename === 'ClientOptimisticUserMessage';
    const isUnavailableTranscript = event.messageBlocks.some(
      (block) =>
        block.__typename === 'DisplayTextBlockNode' && isVoiceTranscriptionUnavailableText(block.text)
    );
    return (
      <ChatMessageBubble
        ref={ref}
        sender={
          messageSenderContext != null ? (
            <ChatUserMessageSender
              creator={resolveUserMessageSenderCreator(event, messageSenderContext, sessionCreatorFallback)}
              senderContext={messageSenderContext}
              isFirstMessageInThread={isFirstMessageInThread}
              showCurrentUserSender={showCurrentUserSender}
            />
          ) : null
        }
        variant="user_message"
        toolbar={null}
        faded={isOptimistic || isUnavailableTranscript}
        footer={
          approvalIndicatorText != null ? (
            <div
              className={classes.approvalIndicator}
              {...getTestProps({component: 'ChatUserMessageBubble', element: 'approval-indicator'})}
            >
              {approvalIndicatorText}
            </div>
          ) : null
        }
      >
        {event.messageBlocks.flatMap((block, index) => {
          switch (block.__typename) {
            case 'DisplayTextBlockNode': {
              // An approval decision made by clicking Allow/Deny carries no user text; only the styled
              // system indicator (footer) is shown. Skip the empty text block so the bubble isn't
              // padded by an empty line.
              if (block.text === '') {
                return [];
              }
              const isUnavailable = isVoiceTranscriptionUnavailableText(block.text);
              return [
                <div
                  key={index}
                  className={isUnavailable ? classes.unavailableTranscript : undefined}
                  {...getTestProps({
                    component: 'ChatUserMessageBubble',
                    element: isUnavailable ? 'unavailable-transcript' : 'text',
                  })}
                >
                  {isUnavailable ? VOICE_TRANSCRIPTION_UNAVAILABLE_TEXT : block.text}
                </div>,
              ];
            }
            case 'DisplayFileBlockNode': {
              return [
                <div key={index} className={classes.chipContainer}>
                  <ChatUploadedFileChip
                    chatSessionId={chatSessionId}
                    // See: createPendingUserMessage. It produces a ClientOptimisticUserMessage,
                    // where the externalFileId is not set.
                    externalFileId={'externalFileId' in block ? block.externalFileId : null}
                    filename={block.filename}
                    mediaType={block.mediaType}
                  />
                </div>,
              ];
            }
            default: {
              assertNever(block);
              return [];
            }
          }
        })}
      </ChatMessageBubble>
    );
  }
);

export default ChatUserMessageBubble;
